import React, { useState, useEffect } from 'react';
import { 
  IconButton, 
  Badge, 
  Menu, 
  MenuItem, 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Button, 
  Stack 
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Info } from 'lucide-react'; 
import { toast } from 'react-toastify';
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import alertaSonido from '../identidad/level-up-3-199576.mp3';

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const PALETTE = {
  ice: "#C2EDF0",
  aqua: "#00C0CA",
  teal: "#008B9C",
  dark: "#22404D",
  red: "#F65F4E",
  slate: "#94A3B8",
  pearl: "#F4F7F9"
};

const Notificacion = () => {
  const [notificaciones, setNotificaciones] = useState([]);
  const [anclajeEl, setAnclajeEl] = useState(null);
  const navigate = useNavigate();

  const manejarMenuAbrir = (event) => setAnclajeEl(event.currentTarget);
  const manejarMenuCerrar = () => setAnclajeEl(null);

  // --- Lógica de SignalR ---
  const conexion = new HubConnectionBuilder()
    .withUrl("https://sistemadesemapideploy.azurewebsites.net/chat")
    .configureLogging(LogLevel.Information)
    .build();

  const conectar = async () => {
    conexion.off("JoinNotifications"); 
    try {
      await conexion.start();
      const deQuien = localStorage.getItem('email');
      await conexion.invoke("JoinNotifications", deQuien);
    } catch (error) {
      console.log('Error en la conexión SignalR:', error);
    }
  };

  const pendientesAMensajes = () => {
    conexion.off("ReceiveSpesificNotification"); 
    conexion.on("ReceiveSpesificNotification", (msg, remitente, id) => {
      const sonido = new Audio(alertaSonido); 
      sonido.play();
      toast.success(msg, {
        position: "bottom-right",
        autoClose: 3000,
        theme: "light",
      });
      cargarMensajes();
    });

    conexion.on("ReceiveSpesificMessage", (msg, remitente, id) => {
      const sonido = new Audio(alertaSonido);
      sonido.play();
      setNotificaciones(prev => [...prev, { msg, remitente, id }]);
    });
  };

  useEffect(() => {
    conectar();
    pendientesAMensajes();
    cargarMensajes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarMensajes = async () => {
    try {
      let respuesta = await fetch(`${apiUrl}/Notificacion/Obtener?mail=${localStorage.getItem('email')}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          'apiKey': localStorage.getItem("apiKey")
        }
      });
      if (respuesta.ok) {
        let datos = await respuesta.json();
        let transformadas = datos.map(n => ({
          Code: n.deQuien,
          msg: n.msgNotificacion,
          id: n.id,
          idAux: n.idAuxiliar
        }));
        setNotificaciones(transformadas);
      }
    } catch (error) {
      console.log('Error al cargar mensajes:', error);
    }
  };

  const manejarNotificacionClic = async (notificacion) => {
    try {
      const respuesta = await fetch(`${apiUrl}/Notificacion/VerMensaje?id=${notificacion.id}`);
      if (respuesta.ok) {
        setNotificaciones(prev => prev.filter(n => n.id !== notificacion.id));
        
        if(notificacion.Code === "Comunicacion"){
          navigate("/pedido/"+ notificacion.idAux);
        }
        else if(notificacion.Code === "Finanzas"){
          navigate("/adelanto/"+notificacion.idAux);
        }
        else if(notificacion.Code === "FinanzasGasto"){
          navigate("/listadoGastosPorPrograma/"+notificacion.idAux);
        }
      }
    } catch (error) {
      console.error("Error al procesar clic:", error);
    }
  };

  const verTodasLasNotificaciones = async () => {
    // Procesamos todas las notificaciones como vistas
    for (const noti of notificaciones) {
      await fetch(`${apiUrl}/Notificacion/VerMensaje?id=${noti.id}`);
    }
    setNotificaciones([]);
  };

  return (
    <Box>
      <IconButton
        onClick={manejarMenuAbrir}
        sx={{ 
          transition: '0.3s',
          "&:hover": { transform: 'scale(1.1)' }
        }}
      >
        <Badge 
          badgeContent={notificaciones.length} 
          sx={{ 
            "& .MuiBadge-badge": { 
              backgroundColor: PALETTE.red, 
              color: 'white',
              fontWeight: 900,
              fontSize: '0.65rem'
            } 
          }}
        >
          <Bell size={24} />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anclajeEl}
        open={Boolean(anclajeEl)}
        onClose={manejarMenuCerrar}
        disableScrollLock={true} // Evita que la página principal se bloquee
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            width: 340,
            maxHeight: 480,
            mt: 1.5,
            borderRadius: '24px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
            border: '1px solid rgba(0,0,0,0.05)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        {/* Cabecera fija */}
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#F8FAFC', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 900, color: PALETTE.dark }}>
            Notificaciones
          </Typography>
          {notificaciones.length > 0 && (
            <IconButton size="small" onClick={verTodasLasNotificaciones} sx={{ color: PALETTE.teal }}>
              <CheckCheck size={18} />
            </IconButton>
          )}
        </Box>

        {/* Área con Scroll */}
        <Box sx={{ p: 1, overflowY: 'auto', flexGrow: 1 }}>
          {notificaciones.length > 0 ? (
            notificaciones.slice().reverse().map((noti, i) => (
              <MenuItem 
                key={i} 
                onClick={() => { manejarMenuCerrar(); manejarNotificacionClic(noti); }}
                sx={{ p: 0, mb: 1, borderRadius: '16px', whiteSpace: 'normal', "&:hover": { bgcolor: 'transparent' } }}
              >
                <Card
                  elevation={0}
                  sx={{
                    width: '100%',
                    borderRadius: '16px',
                    border: '1px solid rgba(0,0,0,0.04)',
                    transition: '0.2s',
                    position: 'relative',
                    "&:hover": { bgcolor: PALETTE.pearl, transform: 'translateX(4px)' }
                  }}
                >
                  <Box sx={{ 
                    position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, 
                    bgcolor: noti.Code === "Finanzas" ? PALETTE.red : PALETTE.aqua 
                  }} />
                  
                  <CardContent sx={{ p: 1.5, pl: 2.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="caption" sx={{ fontWeight: 900, color: PALETTE.dark, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {noti.Code}
                      </Typography>
                      <Info size={14} color={PALETTE.slate} />
                    </Stack>
                    <Typography variant="body2" sx={{ color: '#555', fontSize: "0.8rem", mt: 0.5, lineHeight: 1.3 }}>
                      {noti.msg}
                    </Typography>
                  </CardContent>
                </Card>
              </MenuItem>
            ))
          ) : (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: PALETTE.slate, fontWeight: 600 }}>
                Sin notificaciones nuevas.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Botón inferior fijo */}
        {notificaciones.length > 0 && (
          <Box sx={{ p: 1.5, borderTop: '1px solid rgba(0,0,0,0.05)', bgcolor: 'white' }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => { verTodasLasNotificaciones(); manejarMenuCerrar(); }}
              sx={{ 
                bgcolor: PALETTE.dark, 
                borderRadius: '12px', 
                textTransform: "none", 
                fontWeight: 800,
                fontSize: "0.75rem",
                "&:hover": { bgcolor: PALETTE.teal }
              }}
            >
              Marcar todas como leídas
            </Button>
          </Box>
        )}
      </Menu>
    </Box>
  );
};

export default Notificacion;